import { Stack } from '@mui/material';
import { getPaymentAndDeductions } from 'API/paymentAndDeductions';
import { CustomMetaTitle, LinkReturnToMain } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { useCallback, useEffect, useReducer, useState } from 'react';
import {
  IPaymentsDeductionsState,
  PaymentsDeductionsReducer,
} from 'reducer/PaymentsAndDeductions/PaymentsDeductionsReducer';
import { PaymentsAndDeductionsHeader } from './PaymentsAndDeductionsHeader';
import { PaymentsDeductionList } from './PaymentsDeductionList';
import { SearchFilter } from './SearchFilter';
import { AdvancedSearchContextProvider } from 'Context/PaymentDeductionContext/AdvancedSearchProvider';
import { isAfter } from 'date-fns';
import { useLoader } from 'Hooks/useLoader';
import { NoPaymentsAndDeductionsFound } from './NoPaymentsAndDeductionsFound';

export const PaymentsAndDeductions = () => {
  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  const [paymentsDeductions, dispatchPaymentsDeductions]: [
    IPaymentsDeductionsState,
    Function
  ] = useReducer(PaymentsDeductionsReducer, {
    paymentsDeductions: [],
    filteredPaymentsDeductions: [],
  });

  const [dateMinMaxRange, setdateMinMaxRange] = useState<{
    min: Date;
    max: Date;
  }>({ min: new Date(), max: new Date() });
  const { isLoading, setIsLoading, loader } = useLoader();
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.PaymentsDeductions);

  const initPaymentsDeductions = useCallback(async () => {
    setIsLoading(true);
    const result = await getPaymentAndDeductions();
    if (result) {
      dispatchPaymentsDeductions({
        type: 'INITIALIZATION',
        payload: { value: result },
      });
      const minMaxDate = {
        min: result[0]?.statusDate
          ? new Date(result[0].statusDate)
          : new Date(),
        max: result[0]?.statusDate
          ? new Date(result[0].statusDate)
          : new Date(),
      };
      for (const { statusDate } of result) {
        if (!statusDate) continue;
        const open = new Date(statusDate);
        if (isAfter(minMaxDate.min, open)) minMaxDate.min = new Date(open);
        if (isAfter(open, minMaxDate.max)) minMaxDate.max = new Date(open);
      }
      setdateMinMaxRange(minMaxDate);
    }
    setIsLoading(false);
  }, [setIsLoading]);

  useEffect(() => {
    initPaymentsDeductions();
  }, [initPaymentsDeductions, imposterId]);

  if (isLoading) {
    return (
      <Stack gap={isDesktop ? '54px' : '24px'} marginBottom={'88px'}>
        <CustomMetaTitle title={dictionary.title} />
        <LinkReturnToMain />
        <Stack direction='column' maxWidth='1000px' margin='0 auto 50px auto'>
          <PaymentsAndDeductionsHeader />
          {loader}
        </Stack>
      </Stack>
    );
  }

  if (paymentsDeductions.paymentsDeductions.length === 0)
    return (
      <Stack gap={isDesktop ? '54px' : '24px'} marginBottom={'88px'}>
        <CustomMetaTitle title={dictionary.title} />
        <LinkReturnToMain />
        <NoPaymentsAndDeductionsFound />
      </Stack>
    );

  return (
    <Stack gap={isDesktop ? '54px' : '24px'} marginBottom={'88px'}>
      <CustomMetaTitle title={dictionary.title} />
      <LinkReturnToMain />
      <Stack direction='column' maxWidth='1000px'>
        <AdvancedSearchContextProvider>
          <Stack gap={isDesktop ? '48px' : '24px'}>
            <Stack gap={'40px'}>
              <PaymentsAndDeductionsHeader />
              <SearchFilter
                paymentsDeductions={paymentsDeductions.paymentsDeductions}
                dispatchPaymentsDeductions={dispatchPaymentsDeductions}
                dateMinMaxRange={dateMinMaxRange}
              />
            </Stack>
            <PaymentsDeductionList
              filteredPaymentsDeductions={
                paymentsDeductions.filteredPaymentsDeductions
              }
              dispatchPaymentsDeductions={dispatchPaymentsDeductions}
            />
          </Stack>
        </AdvancedSearchContextProvider>
      </Stack>
    </Stack>
  );
};
